import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { contentDefaults } from '@/lib/contentDefaults'
import type { SiteContent } from '@/lib/types'

interface UseSiteContentReturn {
  content: SiteContent | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to load site content from database with fallback to config files.
 * Falls back to contentDefaults if database is unavailable or content is missing.
 *
 * @returns {UseSiteContentReturn} Object containing:
 *   - content: SiteContent object or null if loading
 *   - loading: Boolean indicating if content is being loaded
 *   - error: Error message string or null
 *   - refetch: Function to manually reload content from database
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { content, loading, error, refetch } = useSiteContent()
 *
 *   if (loading) return <Loader />
 *   if (error) return <Error message={error} />
 *
 *   return <div>{content?.event.coupleNames}</div>
 * }
 * ```
 */
export function useSiteContent(): UseSiteContentReturn {
  const [content, setContent] = useState<SiteContent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadContent = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: dbError } = await supabase
        .from('site_content')
        .select('content')
        .eq('id', 'default')
        .single()

      if (dbError) {
        // Database error - fallback to defaults
        console.warn('Failed to load content from database, using defaults:', dbError)
        setContent(contentDefaults)
        setError(null) // Don't show error to user, fallback is working
      } else if (data?.content) {
        // Successfully loaded from database
        setContent(data.content as unknown as SiteContent)
        setError(null)
      } else {
        // No content in database - use defaults
        console.warn('No content found in database, using defaults')
        setContent(contentDefaults)
        setError(null)
      }
    } catch (err) {
      // Unexpected error - fallback to defaults
      console.error('Error loading site content:', err)
      setContent(contentDefaults)
      setError(null) // Fallback is working, don't show error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadContent()
  }, [loadContent])

  return {
    content,
    loading,
    error,
    refetch: loadContent,
  }
}

